/**
 * The clip library: the clips Forge cut for a campaign, kept on the NAS and
 * handed to creators through signed links that expire.
 *
 * Plain types and constants only — no zod, for the reason every other file in
 * this package gives: the web app imports the barrel and a zod object would
 * ship to the browser. The Worker's request schemas live in
 * `apps/api/src/library/contracts.ts`.
 */

/** One clip, as the campaign page shows it. */
export interface LibraryClip {
  fileName: string;
  sizeBytes: number;
  /** ISO-8601, the file's mtime on the NAS. */
  modifiedAt: string;
  /** Forge's title for the clip, when it wrote one; the file name otherwise. */
  title: string | null;
  /** Forge's one-line description of the clip. */
  caption: string | null;
  /** The ready-to-post caption with its hashtags, from the `.txt` sidecar. */
  postText: string | null;
  /** Without the `#`. */
  hashtags: string[];
  durationSeconds: number | null;
}

/**
 * `GET /campaigns/:slug/clips`.
 *
 * Three answers and no error for the ordinary cases, on purpose: a campaign
 * with no folder is a campaign with no section, and a library that cannot be
 * reached is a sentence on the page — never a broken campaign page. The
 * 2026-09-13 outage is why: the fleet ran blind for fifteen hours and nothing
 * on the creator side is allowed to depend on a box in the office answering.
 */
export type CampaignClipsResponse =
  | { status: 'not_linked' }
  | { status: 'offline' }
  | { status: 'ok'; folderId: string; clips: LibraryClip[]; linkTtlMinutes: number };

/** `POST /campaigns/:slug/clips/link` — one signed download link. */
export interface ClipLinkResponse {
  fileName: string;
  url: string;
  expiresAt: string;
}

/** One entry of the service's allowlist, for the operator's picker. */
export interface LibraryFolder {
  id: string;
  path: string;
  /** Clips in it right now, or null when the folder could not be read. */
  files: number | null;
}

/** `GET /admin/library/folders`. */
export type LibraryFoldersResponse =
  | { status: 'offline' }
  | { status: 'ok'; folders: LibraryFolder[] };

/** `GET`/`PATCH /admin/library/campaigns/:idOrSlug/folder`. */
export interface CampaignFolderResponse {
  folderId: string | null;
}

/** How long a download link is good for. Fifteen minutes: one click's worth. */
export const CLIP_LINK_TTL_MINUTES = 15;

/** What the campaign page says when the library cannot be reached. */
export const CLIP_LIBRARY_OFFLINE_MESSAGE =
  'The clip library is offline right now; the clips are on the way.';

/** The shape of an allowlist id. The service enforces the same pattern. */
export const CLIP_FOLDER_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;
